import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useLayer } from '../../components/layers'
import { PendingButton, useSlow, Failed } from '../../components/pending'
import type React from 'react'
import styles from '../WagesPage.module.css'
import o from '../../components/Overlay.module.css'
import m from './MergeDialog.module.css'
import { PersonAvatar } from './PersonAvatar'
import { apiJson, ApiError } from '../../lib/api'
import { dmy, plural, rangeLabel } from '../wages/format'
import type { BlockingPush } from '../wages/types'
import { fullName, initialsOf, type Person } from './PersonRecordPanel'

// The merge dialog (handoff §7b, 836-848): which of two records survives.
// Nothing is chosen for the admin; once they pick, the server previews that
// direction (what moves, the dates it requeues, or why it would refuse) and
// the merge itself makes the same checks again. The server half is
// apps/api/src/services/worker-merge.js.

interface Preview {
  picks: number
  timesheets: number
  tags: number
  dates: string[]
  // Work on days the alias was already exported on: it stays with the alias (D5.7).
  kept_exported?: { dates: string[]; timesheets: number; picks: number }
}
interface Merged {
  picks_moved: number
  timesheets_moved: number
  tags_moved: number
  queued: number
  kept_exported?: { dates: string[] }
}

interface Props {
  user: any
  people: [Person, Person]
  onClose: () => void
  onMerged: (msg: string) => void
}

const SHOWN_DATES = 8

// 131. The server refuses a merge while a push to a provider covering either
// person's work is unresolved. It says which push, which people and days, and
// whether the push is still being sent or its outcome is unknown. It never
// claims to know more: an unknown push may or may not have reached the provider.
function BlockingPushes({ blocking, onClose }: { blocking: BlockingPush[]; onClose: () => void }) {
  return (
    <div className={o.danger} role="alert" data-testid="merge-blocking">
      <div>
        <strong>This merge is blocked by a push to a payroll provider that is not resolved.</strong> Until it is, the work it covers cannot be moved to another person.
      </div>
      <ul className={m.moves}>
        {blocking.map((b) => {
          const unknown = b.status === 'unknown'
          return (
            <li key={b.attempt_id} data-testid="merge-blocking-push" data-status={b.status}>
              <strong>{unknown ? 'Outcome unknown' : 'Sending / in progress'}</strong> · {b.destination_label} · {rangeLabel(b.period_from, b.period_to)}
              {b.people.map((p) => (
                <div key={p.human_id}>
                  {p.name}: {p.dates.length > SHOWN_DATES ? `${p.dates.slice(0, SHOWN_DATES).map(dmy).join(', ')} and ${p.dates.length - SHOWN_DATES} more` : p.dates.map(dmy).join(', ')}
                </div>
              ))}
              <div>
                {unknown
                  ? `CONTROL does not know whether ${b.destination_label} received it. Someone who can push payroll must check ${b.destination_label} and record what they found.`
                  : 'It is still being sent. Try again when it has finished.'}
              </div>
              <Link to={`/wages?from=${b.period_from}&to=${b.period_to}&attempt=${encodeURIComponent(b.attempt_id)}#push-attempts`} onClick={onClose} className={styles.linkBtn}>
                Open the push record
              </Link>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

const blockingOf = (e: unknown): BlockingPush[] | null => {
  if (!(e instanceof ApiError)) return null
  const b = e.body
  return (b?.code === 'PROVIDER_EXPORT_IN_PROGRESS' || b?.code === 'PROVIDER_EXPORT_OUTCOME_UNKNOWN') && Array.isArray(b.blocking) && b.blocking.length ? b.blocking : null
}

export function MergeDialog({ user, people, onClose, onMerged }: Props) {
  const [masterId, setMasterId] = useState<string | null>(null)
  const [preview, setPreview] = useState<Preview | null>(null)
  const [refusal, setRefusal] = useState<string | null>(null)
  const [blocked, setBlocked] = useState<BlockingPush[] | null>(null)
  const [busy, setBusy] = useState(false)
  const slow = useSlow(busy)
  const [error, setError] = useState<string | null>(null)

  const master = people.find((p) => p.id === masterId) || null
  const alias = master ? people.find((p) => p.id !== masterId)! : null

  useEffect(() => {
    setPreview(null)
    setRefusal(null)
    setBlocked(null)
    setError(null)
    if (!master || !alias) return
    let live = true
    apiJson<Preview>(user, `/api/personnel/merge-preview?alias=${encodeURIComponent(alias.id)}&master=${encodeURIComponent(master.id)}`)
      .then((r) => live && setPreview(r))
      .catch((e: any) => {
        if (!live) return
        const b = blockingOf(e)
        if (b) setBlocked(b)
        setRefusal(e instanceof ApiError ? e.message : e?.message || 'Could not check this merge')
      })
    return () => {
      live = false
    }
  }, [masterId])

  // A layer (h 425-458). Choosing which record stays is not an edit, so it
  // closes without asking (h 3975-4002 busy prevents close).
  const panelEl = useRef<HTMLDivElement>(null)
  const { requestClose } = useLayer(true, () => !busy && onClose(), { el: panelEl, modal: true, busy })

  const merge = async () => {
    if (!master || !alias || !preview || busy) return
    setBusy(true)
    setError(null)
    try {
      const r = await apiJson<Merged>(user, '/api/personnel/merges', { body: { aliasId: alias.id, masterId: master.id } })
      onMerged(
        `${fullName(alias)} merged into ${fullName(master)} · ` +
          `${plural(r.picks_moved, 'pick', 'picks')}, ${plural(r.timesheets_moved, 'timesheet', 'timesheets')}, ${plural(r.tags_moved, 'tag', 'tags')} moved` +
          (r.queued ? ` · ${plural(r.queued, 'worker/day', 'worker/days')} queued for recalculation` : '') +
          (r.kept_exported?.dates.length ? ` · ${plural(r.kept_exported.dates.length, 'day already sent to payroll stays', 'days already sent to payroll stay')} with ${fullName(alias)}` : '')
      )
    } catch (e: any) {
      // A push started between the check and the merge: the same explanation, and the merge stays off.
      const b = blockingOf(e)
      if (b) {
        setBlocked(b)
        setRefusal(e.message)
      } else setError(e?.message || 'The profiles were not merged')
      setBusy(false)
    }
  }

  const dates = preview?.dates || []
  const kept = preview?.kept_exported
  const keptList = kept ? (kept.dates.length > SHOWN_DATES ? `${kept.dates.slice(0, SHOWN_DATES).map(dmy).join(', ')} and ${kept.dates.length - SHOWN_DATES} more` : kept.dates.map(dmy).join(', ')) : ''
  const dateList = dates.length > SHOWN_DATES ? `${dates.slice(0, SHOWN_DATES).map(dmy).join(', ')} and ${dates.length - SHOWN_DATES} more` : dates.map(dmy).join(', ')

  const meta = (p: Person) =>
    [p.email || 'No email', p.employee_id ? `ID ${p.employee_id}` : 'No employee ID', p.last_active ? `Last active ${dmy(p.last_active)}` : 'No shifts recorded'].join(' · ')

  return (
    <div className={o.scrim} onClick={(e) => e.target === e.currentTarget && requestClose()}>
      <div ref={panelEl} tabIndex={-1} className={o.modal} role="dialog" aria-label="Merge two profiles">
        <div className={o.head}>
          <div className={o.headText}>
            <h2 className={o.title}>Merge two profiles</h2>
            <p className={o.sub}>
              Same person, two sign-ups. Choose which record survives. All picks, timesheets and assignments move onto it, and wages stop being treated as different people.
            </p>
          </div>
          <button type="button" className={o.close} aria-label="Close" disabled={busy} onClick={requestClose}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className={o.body}>
          <p className={o.label} id="merge-master-label">
            Keep as master profile
          </p>
          <div className={m.cards} role="radiogroup" aria-labelledby="merge-master-label">
            {people.map((p) => {
              const on = p.id === masterId
              return (
                <label key={p.id} className={on ? `${o.choice} ${o.choiceOn}` : o.choice} data-testid="merge-card">
                  <input className={m.hiddenRadio} type="radio" name="merge-master" checked={on} onChange={() => setMasterId(p.id)} aria-label={`Keep ${fullName(p)}`} />
                  <span className={o.radio} aria-hidden="true" />
                  <PersonAvatar id={p.id} photoAt={p.photo_at} initials={initialsOf(p)} className={m.avatar} />
                  <span className={m.who}>
                    <span className={m.cardName}>{fullName(p)}</span>
                    <span className={m.cardMeta}>{meta(p)}</span>
                  </span>
                  {masterId && <span className={on ? `${o.tag} ${o.tagOn}` : o.tag}>{on ? 'Master' : 'Becomes alias'}</span>}
                </label>
              )
            })}
          </div>

          {master && alias && (
            <>
              {preview && (
                <>
                  <ul className={`${o.note} ${m.moves}`} data-testid="merge-consequences">
                    <Tick>
                      {plural(preview.picks, 'pick', 'picks')}, {plural(preview.timesheets, 'timesheet', 'timesheets')} and {plural(preview.tags, 'QR tag', 'QR tags')} move from{' '}
                      {fullName(alias)} onto {fullName(master)}.
                    </Tick>
                    <Tick>Wages recalculate them as one person, so the two records stop being paid as different people.</Tick>
                    {kept && kept.dates.length > 0 && (
                      <Tick testId="merge-exported">
                        {plural(kept.dates.length, 'day', 'days')} already sent to payroll ({keptList}) stay with {fullName(alias)}: their pay is not moved, recalculated or rewritten. Later days
                        follow {fullName(master)}.
                      </Tick>
                    )}
                    <Tick>A device that still names {fullName(alias)} credits {fullName(master)}.</Tick>
                    <Tick testId="merge-dates">
                      {dates.length ? <>Both records are recalculated for {plural(dates.length, 'day', 'days')}: {dateList}.</> : 'No work moves, so no day is recalculated.'}
                    </Tick>
                  </ul>
                  <div className={o.warn}>
                    <WarnIcon />
                    <span>
                      {fullName(alias)} becomes an alias and stops appearing in lists. The merge can be undone from {fullName(master)}’s merge history.
                    </span>
                  </div>
                </>
              )}
              {!preview && !refusal && <div className={m.checking}>Checking…</div>}
              {refusal && blocked && <BlockingPushes blocking={blocked} onClose={onClose} />}
              {refusal && !blocked && (
                <div className={o.danger} role="alert">
                  <WarnIcon />
                  <span>{refusal}</span>
                </div>
              )}
            </>
          )}
        </div>

        <div className={o.foot}>
          <button type="button" className={o.ghost} disabled={busy} onClick={requestClose}>
            Cancel
          </button>
          <PendingButton
            className={o.primary}
            busy={busy}
            pending="Merging…"
            disabled={!preview || refusal !== null}
            title={!masterId ? 'Choose the record that stays' : refusal ? 'This merge cannot be made' : undefined}
            onClick={merge}
          >
            {master ? `Merge into ${fullName(master)}` : 'Merge profiles'}
          </PendingButton>
        </div>
        <Failed text={error} />
      </div>
    </div>
  )
}

function Tick({ children, testId }: { children: React.ReactNode; testId?: string }) {
  return (
    <li className={o.noteLine} data-testid={testId}>
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M20 6 9 17l-5-5" />
      </svg>
      <span>{children}</span>
    </li>
  )
}

function WarnIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0ZM12 9v4M12 17h.01" />
    </svg>
  )
}
